/**
 * 카테고리 선택 모달 (제품 에디터).
 * Figma: 307:2363 (empty state), 298:9116 (선택된 상태).
 *
 * 구조:
 *  - 모달 960×720, 타이틀 "카테고리 선택" + close X
 *  - body 좌/우 분할 (각 480w)
 *    - 좌측: 검색 + 전체선택/모두펼치기 도구 + 카테고리 트리(체크박스)
 *    - 우측: 선택된 카테고리 chip 리스트 (비어있으면 빈 상태 텍스트)
 *           + 하단 액션 버튼 (전체 해제 + 선택 완료)
 *
 * 카테고리 트리:
 *  - depth 1 (루트): white bg, border-bottom Light, 접기/펼치기 가능
 *  - depth 2+: paper bg, indent (figma 100px padding-left)
 *  - parentCategoryId 로 트리 구성
 *
 * Suspect:
 *  - 검색 시 매칭된 항목 + 부모 노출 정책 (정확한 동작 figma 미정의)
 *  - depth 3+ indent 정확한 수치 (figma 2-depth 만 확인)
 *  - "전체 선택" 인디터미네이트 상태 figma 미확인
 *  - 선택 chip 의 path 구조 — parentCategoryId 따라가서 "대>중>소" 조립 필요
 */

import React, { useState, useMemo, useCallback, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faMinus } from "@fortawesome/free-solid-svg-icons";
import { ChevronDown, ChevronUp } from "lucide-react";

import {
  DSModal,
  DSButton,
  DSSearchInput,
  DSCheckbox,
  DSCategoryChip,
} from "./ds";

// ---------------------------------------------------------------------
// Helpers — 카테고리 트리 빌드 + 경로 추출
// ---------------------------------------------------------------------
const buildTree = (categoriesById) => {
  const cats = Object.values(categoriesById || {});
  const byParent = {};
  cats.forEach((c) => {
    const parentKey = c.parentCategoryId || "__root__";
    if (!byParent[parentKey]) byParent[parentKey] = [];
    byParent[parentKey].push(c);
  });
  Object.values(byParent).forEach((arr) =>
    arr.sort((a, b) => (a.name || "").localeCompare(b.name || "")),
  );
  return byParent;
};

const getPathSegments = (categoriesById, id) => {
  const segments = [];
  let curId = id;
  // 4 depth 최대 (notion 정책)
  for (let i = 0; i < 6 && curId; i++) {
    const cat = (categoriesById || {})[curId];
    if (!cat) break;
    segments.unshift(cat.name || "(이름 없음)");
    curId = cat.parentCategoryId;
  }
  return segments;
};

// 외부에서 동일 로직 사용 (제품 리스트, 에디터)
export { getPathSegments };

const getAllDescendantIds = (byParent, id) => {
  const ids = [];
  const stack = [id];
  while (stack.length) {
    const cur = stack.pop();
    const children = byParent[cur] || [];
    for (const c of children) {
      ids.push(c.id);
      stack.push(c.id);
    }
  }
  return ids;
};

// cascade 모드용: 해당 노드의 leaf(자식 없는) 후손 id. 노드 자신이 leaf 면 [id].
const getLeafIds = (byParent, id) => {
  const children = byParent[id] || [];
  if (children.length === 0) return [id];
  let leaves = [];
  for (const c of children) leaves = leaves.concat(getLeafIds(byParent, c.id));
  return leaves;
};

// ---------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------

/** 인라인 체크박스 시각 (DSCheckbox 의 label 이중 click 문제 회피용)
 *  partial = cascade 모드에서 일부 자식만 선택된 부모 (minus 표시) */
const CheckboxVisual = ({ checked, partial = false }) => (
  <span
    className={`ds-checkbox__box ${
      checked
        ? "ds-checkbox__box--checked"
        : partial
        ? "ds-checkbox__box--partial"
        : ""
    }`}
  >
    {checked && <FontAwesomeIcon icon={faCheck} style={{ fontSize: 10 }} />}
    {!checked && partial && <FontAwesomeIcon icon={faMinus} style={{ fontSize: 10 }} />}
  </span>
);

/** depth 1 카테고리 행 (figma 307:2113)
 *  버그 수정: row 전체 click → check 토글. chevron 만 expand 토글.
 */
const CategoryRow = ({
  category,
  childrenCount,
  selectedDescendantCount,
  isExpanded,
  isChecked,
  isPartial = false,
  isDisabled = false,
  onToggleCheck,
  onToggleExpand,
  meta = null,
}) => {
  const hasChildren = childrenCount > 0;
  return (
    <div
      className={`ds-category-row ${
        isDisabled ? "ds-category-row--disabled" : ""
      }`}
      onClick={isDisabled ? undefined : onToggleCheck}
    >
      <div className="ds-category-row__left">
        <CheckboxVisual checked={isChecked} partial={isPartial} />
        <span className="ds-checkbox__label">{category.name}</span>
        {meta && <span className="ds-category-row__meta">{meta}</span>}
      </div>
      <div className="ds-category-row__right">
        {hasChildren && (
          // 숫자 + chevron 전체를 하나의 펼침 버튼으로 (클릭 영역 확대)
          <button
            type="button"
            className="ds-category-row__expand"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            aria-label={isExpanded ? "접기" : "펼치기"}
          >
            <span className="ds-category-row__count">
              {selectedDescendantCount}{" "}
              <span className="ds-category-row__count-total">/ {childrenCount}</span>
            </span>
            {/* 접힘 = ▼(아래), 펼침 = ▲(위) */}
            {isExpanded ? (
              <ChevronUp size={14} className="ds-category-row__chevron" />
            ) : (
              <ChevronDown size={14} className="ds-category-row__chevron" />
            )}
          </button>
        )}
      </div>
    </div>
  );
};

/** depth 2+ 자식 row (figma 307:1972 — pl 100, py 12)
 *  중분류/소분류도 자식이 있으면 대분류처럼 우측에 "선택수/전체 + chevron" 펼침 버튼 노출. */
const CategoryChildRow = ({
  category,
  depth,
  childrenCount = 0,
  selectedDescendantCount = 0,
  isExpanded = false,
  isChecked,
  isPartial = false,
  isLocked = false,
  isDisabled = false,
  onToggleCheck,
  onToggleExpand,
  meta = null,
}) => {
  // figma(307:1971): 중분류(depth2) pl 64, 소분류(depth3) pl 100. step 36.
  const paddingLeft = 64 + (depth - 2) * 36;
  const hasChildren = childrenCount > 0;
  return (
    <div
      className={`ds-category-child ${
        isLocked ? "ds-category-child--locked" : ""
      } ${isDisabled ? "ds-category-row--disabled" : ""}`}
      style={{ paddingLeft }}
      onClick={isLocked || isDisabled ? undefined : onToggleCheck}
    >
      <div className="ds-category-child__inner">
        <CheckboxVisual checked={isChecked} partial={isPartial} />
        <span className="ds-checkbox__label">{category.name}</span>
        {isLocked && <span className="ds-category-row__lock">필수</span>}
        {meta && <span className="ds-category-row__meta">{meta}</span>}
      </div>
      {hasChildren && (
        <button
          type="button"
          className="ds-category-row__expand"
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand && onToggleExpand();
          }}
          aria-label={isExpanded ? "접기" : "펼치기"}
        >
          <span className="ds-category-row__count">
            {selectedDescendantCount}{" "}
            <span className="ds-category-row__count-total">/ {childrenCount}</span>
          </span>
          {isExpanded ? (
            <ChevronUp size={14} className="ds-category-row__chevron" />
          ) : (
            <ChevronDown size={14} className="ds-category-row__chevron" />
          )}
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------
const CategoryModal = ({
  open,
  onClose,
  categoriesById = {},
  selectedIds = [],
  onApply,
  // 사용처별 커스터마이즈 (제품 에디터 vs 필터 컨텍스트)
  title = "카테고리 선택",
  // applyLabel:
  //   - string             → 고정 라벨 (예: 필터 컨텍스트의 "필터 적용")
  //   - (count) => string  → 동적 라벨 (예: 리포트 빌더의 "N개 항목 선택")
  //   - 미지정             → figma 기본 동적 라벨 "X개 카테고리 선택" (제품 에디터)
  applyLabel = null,
  // (id) => "N개 제품" 같은 부가 표기. row name 뒤 + chip 의 마지막 segment 뒤에 노출.
  getCategoryMeta = null,
  // 문맥별 문구 (기본값 = 제품 카테고리. 리포트 빌더 등에서 "항목"으로 교체)
  searchPlaceholder = "카테고리 검색",
  selectAllLabel = (selected, total) => `전체 선택 (${selected}/${total})`,
  emptyText = "선택된 카테고리가 없습니다.",
  clearLabel = "전체 해제",
  // cascade=true (리포트 빌더): 부모 선택 시 하위 leaf 전체 선택/해제, 부모는 leaf 상태에서
  //   checked/partial 파생. 선택 집합엔 leaf 만 담김(부모 id 미포함). chip/저장도 leaf 기준.
  //   기본 false = 제품 카테고리(각 노드 독립 선택).
  cascade = false,
  // 필수 항목(leaf id): 항상 선택 상태로 표시되고 해제 불가(disable). 리포트 타입 기준 항목.
  lockedIds = [],
  // 선택 상한 (null = 무제한). 초과 선택은 무시한다 — 리포트 카테고리 믹스 5열 상한처럼
  // 화면이 감당할 수 있는 개수가 정해진 경우 [우진 2026-08-10]. 기존 사용처는 미지정.
  maxSelected = null,
  // 단일 선택 모드 — 새로 고른 항목이 기존 선택을 덮는다 (라디오처럼). 할인 카테고리처럼
  // 저장 필드가 단일 ID 인 경우에 쓴다. maxSelected=1 은 "초과 선택 무시"라 동작이 다르다.
  singleSelect = false,
  // 선택 허용 목록(화이트리스트). null = 제한 없음. 목록 밖 카테고리는 비활성으로 렌더된다.
  // 카테고리 조합 할인의 "할인 카테고리"를 "구매 카테고리" 안에서만 고르게 할 때 쓴다.
  allowedIds = null,
}) => {
  const [tempSelected, setTempSelected] = useState(() => new Set(selectedIds));
  const [expanded, setExpanded] = useState(() => new Set());
  const [search, setSearch] = useState("");
  // 모두 펼치기/접기 버튼 라벨 source of truth.
  // expanded.size 가 0 일 때만 "모두 펼치기" 가 아니라, 명시적 boolean 으로 추적.
  // 이유: 자식 없는 카테고리만 있을 때 expand 시도해도 set 이 비어있어 라벨 안 바뀌는 버그.
  const [allExpanded, setAllExpanded] = useState(false);

  // 모달 열릴 때 초기 selectedIds 동기화
  useEffect(() => {
    if (open) {
      setTempSelected(new Set(selectedIds));
      setExpanded(new Set());
      setAllExpanded(false);
      setSearch("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const lockedSet = useMemo(() => new Set(lockedIds), [lockedIds]);
  const allowedSet = useMemo(
    () => (allowedIds ? new Set(allowedIds) : null),
    [allowedIds],
  );
  const isDisabledId = useCallback(
    (id) => !!allowedSet && !allowedSet.has(id),
    [allowedSet],
  );

  const byParent = useMemo(() => buildTree(categoriesById), [categoriesById]);
  const rootCats = byParent["__root__"] || [];
  const allCats = useMemo(() => Object.values(categoriesById || {}), [categoriesById]);

  // 검색 필터링: 매칭된 항목 + 그 항목의 부모/자손 표시
  const matched = useMemo(() => {
    if (!search.trim()) return null;
    const lowerSearch = search.toLowerCase();
    const matchedIds = new Set();
    allCats.forEach((c) => {
      if ((c.name || "").toLowerCase().includes(lowerSearch)) {
        matchedIds.add(c.id);
        // 부모 체인 추가
        let parentId = c.parentCategoryId;
        while (parentId) {
          matchedIds.add(parentId);
          parentId = (categoriesById[parentId] || {}).parentCategoryId;
        }
      }
    });
    return matchedIds;
  }, [search, allCats, categoriesById]);

  // 전체 선택 계산. cascade 면 leaf 만 선택 대상.
  const allIds = useMemo(() => allCats.map((c) => c.id), [allCats]);
  const allLeafIds = useMemo(
    () => allCats.filter((c) => (byParent[c.id] || []).length === 0).map((c) => c.id),
    [allCats, byParent],
  );
  const selectableIds = cascade ? allLeafIds : allIds;
  const isAllSelected =
    selectableIds.length > 0 && selectableIds.every((id) => tempSelected.has(id));

  // cascade 모드: 노드의 checked/partial 을 leaf 선택 상태에서 파생.
  const nodeState = useCallback(
    (id) => {
      if (!cascade) return { checked: tempSelected.has(id), partial: false };
      const children = byParent[id] || [];
      if (children.length === 0)
        return { checked: tempSelected.has(id), partial: false };
      const leaves = getLeafIds(byParent, id);
      const sel = leaves.filter((l) => tempSelected.has(l)).length;
      return {
        checked: sel > 0 && sel === leaves.length,
        partial: sel > 0 && sel < leaves.length,
      };
    },
    [cascade, byParent, tempSelected],
  );

  // 핸들러
  const handleToggleCheck = useCallback(
    (id) => {
      // 잠긴(필수) leaf 단독 토글은 무시.
      if (lockedSet.has(id)) return;
      // 허용 목록 밖은 선택 불가 (해제도 불가 — 애초에 들어갈 수 없다)
      if (allowedSet && !allowedSet.has(id)) return;
      setTempSelected((prev) => {
        const next = new Set(prev);
        if (cascade) {
          // 부모 클릭 = 하위 leaf 전체 선택/해제. leaf 클릭 = 자신만 토글.
          // 단, 잠긴 leaf 는 해제 대상에서 제외(항상 유지).
          const leaves = getLeafIds(byParent, id);
          const toggleable = leaves.filter((l) => !lockedSet.has(l));
          const allSel =
            toggleable.length > 0 && toggleable.every((l) => next.has(l));
          toggleable.forEach((l) => (allSel ? next.delete(l) : next.add(l)));
        } else if (next.has(id)) {
          next.delete(id);
        } else if (singleSelect) {
          // 단일 선택: 새 선택이 기존 선택을 덮는다 (우진 2026-08-11).
          // 상한(maxSelected)처럼 무시하면 먼저 해제해야 해서 라디오처럼 동작하지 않는다.
          next.clear();
          lockedSet.forEach((locked) => next.add(locked));
          next.add(id);
        } else {
          // 상한 도달 시 추가 선택 무시 (해제는 항상 가능)
          if (maxSelected != null && next.size >= maxSelected) return prev;
          next.add(id);
        }
        return next;
      });
    },
    [cascade, byParent, lockedSet, maxSelected, singleSelect, allowedSet],
  );

  const handleToggleExpand = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(
    (checked) => {
      // 해제해도 잠긴 항목은 유지. 상한 모드면 앞에서 상한까지만 (버튼도 숨기지만
      // 프로그램 경로로 들어와도 상한을 넘기지 않는다)
      if (!checked) return setTempSelected(new Set(lockedSet));
      const ids = maxSelected != null ? selectableIds.slice(0, maxSelected) : selectableIds;
      return setTempSelected(new Set(ids));
    },
    [selectableIds, lockedSet, maxSelected],
  );

  const handleToggleAllExpand = useCallback(() => {
    setAllExpanded((prevAll) => {
      const nextAll = !prevAll;
      if (nextAll) {
        // 모두 펼치기 — 자식 있는 모든 카테고리 expand
        const next = new Set();
        allCats.forEach((c) => {
          if ((byParent[c.id] || []).length > 0) next.add(c.id);
        });
        setExpanded(next);
      } else {
        setExpanded(new Set());
      }
      return nextAll;
    });
  }, [allCats, byParent]);

  const handleClearAll = useCallback(
    () => setTempSelected(new Set(lockedSet)),
    [lockedSet],
  );

  const handleApply = useCallback(() => {
    onApply && onApply(Array.from(tempSelected));
    onClose && onClose();
  }, [onApply, onClose, tempSelected]);

  // 선택 chip path 빌드
  const selectedChips = useMemo(
    () =>
      Array.from(tempSelected).map((id) => ({
        id,
        segments: getPathSegments(categoriesById, id),
      })),
    [tempSelected, categoriesById],
  );

  // 검색 필터 적용된 표시 여부
  const shouldShow = (id) => !matched || matched.has(id);

  // 재귀: 자식 트리 렌더
  const renderChildren = (parentId, depth) => {
    const children = (byParent[parentId] || []).filter((c) => shouldShow(c.id));
    if (children.length === 0) return null;
    return (
      <div className="ds-category-children">
        {children.map((child) => {
          const grandchildren = byParent[child.id] || [];
          const descendantIds = getAllDescendantIds(byParent, child.id);
          const selectedCount = descendantIds.filter((id) =>
            tempSelected.has(id),
          ).length;
          return (
            <React.Fragment key={child.id}>
              <CategoryChildRow
                category={child}
                depth={depth}
                childrenCount={grandchildren.length}
                selectedDescendantCount={selectedCount}
                isExpanded={expanded.has(child.id)}
                isChecked={nodeState(child.id).checked}
                isPartial={nodeState(child.id).partial}
                isLocked={lockedSet.has(child.id)}
                isDisabled={isDisabledId(child.id)}
                onToggleCheck={() => handleToggleCheck(child.id)}
                onToggleExpand={() => handleToggleExpand(child.id)}
                meta={getCategoryMeta ? getCategoryMeta(child.id) : null}
              />
              {/* 자식의 자식이 있고 expanded면 재귀 (소분류 노출) */}
              {grandchildren.length > 0 &&
                expanded.has(child.id) &&
                renderChildren(child.id, depth + 1)}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <DSModal open={open} onClose={onClose} title={title} width={960} height={720}>
      <div className="ds-category-modal">
        {/* === 좌측: 카테고리 트리 === */}
        <div className="ds-category-modal__left">
          <div className="ds-category-modal__tools">
            <DSSearchInput
              value={search}
              onChange={setSearch}
              placeholder={searchPlaceholder}
            />
            <div className="ds-category-modal__tools-row">
              {/* 상한/단일 모드에선 「전체 선택」이 성립하지 않는다 — 대신 안내 문구 */}
              {singleSelect ? (
                <span className="ds-category-modal__limit-note">
                  1개만 선택할 수 있어요
                </span>
              ) : maxSelected == null ? (
                <DSCheckbox
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  label={selectAllLabel(tempSelected.size, selectableIds.length)}
                />
              ) : (
                <span className="ds-category-modal__limit-note">
                  최대 {maxSelected}개 선택 ({tempSelected.size}/{maxSelected})
                </span>
              )}
              <button
                type="button"
                className="ds-collapse-button"
                onClick={handleToggleAllExpand}
              >
                {allExpanded ? "모두 접기" : "모두 펼치기"}
              </button>
            </div>
          </div>
          <div className="ds-category-modal__list">
            {rootCats.filter((c) => shouldShow(c.id)).map((root) => {
              const children = byParent[root.id] || [];
              const descendantIds = getAllDescendantIds(byParent, root.id);
              const selectedCount = descendantIds.filter((id) =>
                tempSelected.has(id),
              ).length;
              return (
                <React.Fragment key={root.id}>
                  <CategoryRow
                    category={root}
                    childrenCount={children.length}
                    selectedDescendantCount={selectedCount}
                    isExpanded={expanded.has(root.id)}
                    isChecked={nodeState(root.id).checked}
                    isPartial={nodeState(root.id).partial}
                    isDisabled={isDisabledId(root.id)}
                    onToggleCheck={() => handleToggleCheck(root.id)}
                    onToggleExpand={() => handleToggleExpand(root.id)}
                    meta={getCategoryMeta ? getCategoryMeta(root.id) : null}
                  />
                  {expanded.has(root.id) && renderChildren(root.id, 2)}
                </React.Fragment>
              );
            })}
            {rootCats.length === 0 && (
              <div
                style={{
                  padding: 40,
                  textAlign: "center",
                  color: "var(--ds-text-tertiary)",
                  fontSize: 14,
                  lineHeight: "22px",
                }}
              >
                카테고리가 없습니다.
              </div>
            )}
          </div>
        </div>

        {/* === 우측: 선택된 카테고리 + 액션 === */}
        <div className="ds-category-modal__right">
          {selectedChips.length === 0 ? (
            <div className="ds-category-modal__empty">
              {emptyText}
            </div>
          ) : (
            // figma 307:5165 — 한 chip 당 한 행, gap 8, padding 16, 우측 패널 안에서 overflow-y auto.
            <div className="ds-category-modal__selected">
              {selectedChips.map((chip) => (
                <DSCategoryChip
                  key={chip.id}
                  segments={chip.segments}
                  onDelete={() => handleToggleCheck(chip.id)}
                />
              ))}
            </div>
          )}
          <div className="ds-category-modal__actions">
            <DSButton
              variant="secondary"
              size="lg"
              onClick={handleClearAll}
              disabled={tempSelected.size <= lockedSet.size}
              style={{ width: 120 }}
            >
              {clearLabel}
            </DSButton>
            <DSButton
              variant="primary"
              size="lg"
              onClick={handleApply}
              style={{ flex: 1 }}
            >
              {typeof applyLabel === "function"
                ? applyLabel(tempSelected.size)
                : applyLabel || `${tempSelected.size}개 카테고리 선택`}
            </DSButton>
          </div>
        </div>
      </div>
    </DSModal>
  );
};

export default React.memo(CategoryModal);
